import React, { useEffect } from "react";
import { connect } from "react-redux";
import { Layout, Button, Row, Col, Card, Input, Spin, Table, Modal, Typography, notification, Select, Tooltip, Radio } from 'antd';
import { CheckOutlined, DeleteTwoTone, EditTwoTone, InfoCircleOutlined, LinkOutlined, MailOutlined, PlusOutlined, SaveOutlined, SendOutlined, UserOutlined } from '@ant-design/icons';
import { InputMask, FormAddress, Form, FormatDate, StatusBadge } from "components";
import { useParams, useNavigate } from "react-router-dom";
import { deleteCompanyShareholder, saveCompanyShareholder, getCompanyShareholder, updateCompanyShareholder, sendBiometricEmailToShareholder } from "services/company-service";
import { ANNUAL_REVENUE_RANGE_DATA, biometricStatus, kycStatus, MONTHLY_REVENUE_RANGE_DATA, SHAREHOLDER_TYPE_DATA } from "utils/static-data";
import moment from "moment";
import { DatePicker } from "components";

const { Footer, Content } = Layout;

const mapStateToProps = (state) => ({
    login: state.authReducer.loginReducer
});

const DataFetch = {
    data: [],
    loading: false
}

const PF = {
    name: 'Pessoa Fisica',
    code: 'F'
};
const PJ = {
    name: 'Pessoa Juridica',
    code: 'J'
};

const CompanyShareholderScreen = React.forwardRef((props, ref) => {
    const { compact = false, company = null, loading = false, formCompany = null, onPostLoad } = props;
    const { id = company } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [formEdit] = Form.useForm();
    const [data, setData] = React.useState(DataFetch);
    const [openAdd, setOpenAdd] = React.useState(false);
    const [btnLoading, setBtnLoading] = React.useState(false);
    const [openEdit, setOpenEdit] = React.useState(false);
    const [editingShareholder, setEditingShareholder] = React.useState(null);
    const [shareholderID, setShareholderID] = React.useState(null);
    const [birthday, setBirthday] = React.useState(null);
    const [legalEntity, setLegalEntity] = React.useState(PF.code);
    const [showLegalEntityModal, setShowLegalEntityModal] = React.useState(false);

    const handleDateChange = (date, dateString) => {
        setBirthday(date);
    };

    const onValidateCompany = async () => {
        try {
            return await formCompany.validateFields();
        } catch (ex) {
            notification.warning({ message: 'Valide os dados da empresa antes de inserir um sócio.' })
        }
        return null;
    }

    const onShowAdd = async (open) => {
        if (open && formCompany !== null && !await onValidateCompany()) {
            return;
        }
        if (open)
            form.resetFields()
        setOpenAdd(open);
    }

    const load = async () => {
        if (id) {
            setData(prev => ({ ...prev, loading: true }))
            const res = await getCompanyShareholder(id);
            if (res) {
                setData({ loading: false, data: res })
                if (onPostLoad) {
                    onPostLoad(res);
                }
            }
        }
    }

    const cleanOnlyNumbers = (value) => {
      return value ? value.replace(/\D/g, "") : null;
    };

    const onSave = async (values) => {
        setBtnLoading(true);
        const payload = {
            ...values,
            cpf: cleanOnlyNumbers(values.cpf),
            cnpj: cleanOnlyNumbers(values.cnpj),
            phone: cleanOnlyNumbers(values.phone),
        };

        try {
            if (await saveCompanyShareholder(id, payload, formCompany ? await onValidateCompany() : null)) {
                await load()
                onShowAdd(false);
            }
        } finally {
            setBtnLoading(false);
        }
    }

    const sendEmail = async (shareholder) => {
        try {
            if(!shareholder || !shareholder.id) {
                notification.error({ message: "Erro ao enviar link ao sốcio, por favor atualize a tela e tente novamente" });
                return;
            }
            await sendBiometricEmailToShareholder(id, shareholder.id);
            notification.success({ message: "Link enviado via email para o sócio!" });
        } catch (err) {
            notification.error({ message: "Erro ao enviar link ao sócio" });
            console.error(err);
        }
    }

    const onCancelAdd = () => {
        form.resetFields();
        setShareholderID(null);
        setLegalEntity(PF.code);
        setOpenAdd(false);
    };

    const onCancelEdit = () => {
        formEdit.resetFields();
        setShareholderID(null);
        setOpenEdit(false);
    };

    const onRemove = async (value) => {
        setData(prev => ({ ...prev, loading: true }))
        await deleteCompanyShareholder(value)
            .then(() => {
                notification.success({ message: 'Sócio removido com sucesso.' })
            });
        await load();
    }

    const onEdit = async (shareholder, shareholderID) => {
        setEditingShareholder(shareholder);
        setShareholderID(shareholderID)
        setOpenEdit(true);
        let relationshipType = null;
        if(shareholder.shareholderType === "l") {
            relationshipType = "legal_representative"
        } else if(shareholder.shareholderType === "r") {
            relationshipType = "attorney_in_fact"
        }
        if(shareholder.cpf) {
            setLegalEntity(PF.code);
        } else {
            setLegalEntity(PJ.code);
        }
        formEdit.setFieldsValue({
            name: shareholder.name,
            cpf: shareholder.cpf,
            address: shareholder.address,
            mother: shareholder.mother,
            birthday: moment(shareholder.birthday),
            surname: shareholder.surname,
            shareholderType: shareholder.shareholderType,
            cnpj: shareholder.cnpj,
            annualRevenueRange: shareholder.annualRevenueRange,
            monthlyRevenueRange: shareholder.monthlyRevenueRange,
            relationshipType: relationshipType,
            email: shareholder.email,
            phone: formatBrazilPhone(shareholder.phone)
        });
    }

    const handleEdit = async (shareholder) => {
        setBtnLoading(true);
        const payload = {
            ...shareholder,
            cpf: cleanOnlyNumbers(shareholder.cpf),
            cnpj: cleanOnlyNumbers(shareholder.cnpj),
            phone: cleanOnlyNumbers(shareholder.phone),
        };
        await updateCompanyShareholder(id, shareholderID, payload, formCompany ? await onValidateCompany() : null)
            .then(() => {
                notification.success({ message: 'Sócio editado com sucesso.' })
            });
        await load();
        setBtnLoading(false);
        setOpenEdit(false);
    }

    const formatBrazilPhone = (phone) => {
        if (!phone) return null;

        const digits = phone.replace(/\D/g, "");
        const normalized = digits.startsWith("55") ? digits.slice(2) : digits;

        if (normalized.length !== 11) return phone;

        const ddd = normalized.slice(0, 2);
        const first = normalized.slice(2, 7);
        const last = normalized.slice(7);

        return `+55 (${ddd}) ${first}-${last}`;
    };

    const isRepresentativeLegal = (shareholder) => {
        return shareholder.shareholderType.code === "l" || shareholder.shareholderType.code === "r";
    }

    const shareholderStatus = (shareholder) => {
        if(shareholder.kycStatus == "R") {
            return kycStatus[shareholder.kycStatus];
        } else if(reasons(shareholder).length > 0) {
            return biometricStatus["R"];
        } else {
            return biometricStatus[shareholder.biometricStatus];
        }
    }

    const reasons = (shareholder) => {
        const reasons = []
        if(shareholder.biometricStatusReasons && shareholder.biometricStatusReasons.length > 0) {
            reasons.filter(reason => reason != null).push(...shareholder.biometricStatusReasons.map(reason => reason + " (biometria)"));
        }
        if(shareholder.kycReasons && shareholder.kycReasons.length > 0) {
            reasons.filter(reason => reason != null).push(...shareholder.kycReasons.map(reason => reason + " (kyc)"));
        }
        return reasons;
    }

    const STATUS_CONFIG = {
        N: { color: "#8c8c8c", icon: <UserOutlined />, label: "Biometria: Não iniciado" },
        C: { color: "#8c8c8c", icon: <UserOutlined />, label: "Biometria: Não iniciado" },
        I: { color: "#faad14", icon: <UserOutlined />, label: "Biometria: Em análise" },
        A: { color: "#52c41a", icon: <UserOutlined />, label: "Biometria: Aprovado" },
        R: { color: "#ff4d4f", icon: <UserOutlined />, label: "Biometria: Reprovado" },
        E: { color: "#fa541c", icon: <UserOutlined />, label: "Biometria: Expirado" },
    };

    const BiometricStatusIcon = ({ status, size = 18 }) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG["N"];

        return (
            <Tooltip title={config.label}>
            <span style={{ color: config.color, fontSize: size }}>
                {config.icon}
            </span>
            </Tooltip>
        );
    };

    React.useImperativeHandle(ref, () => {
        return {
            submit() {
                return true
            }
        };
    });

    useEffect(() => {
        load()
    }, [id])

    return (
        <React.Fragment>
            <Modal
                title="Inserir um novo sócio"
                open={openAdd}
                okText={'Salvar novo sócio'}
                cancelText={'Voltar'}
                onOk={() => { form.submit() }}
                okButtonProps={{
                    icon: <SaveOutlined />,
                    disabled: loading,
                    loading: btnLoading
                }}
                confirmLoading={false}
                onCancel={() => onCancelAdd()}
                width={800}
            >
                <Spin spinning={loading}>
                    {shareholderID == null &&
                        <Row gutter={[24, 24]} align="bottom" justify="center" style={{marginBottom: "10px"}}>
                            <Col xl={24} style={{ textAlign: "center" }}>
                                <Radio.Group
                                    disabled={loading}
                                    options={[
                                    { label: PF.name, value: PF.code },
                                    { label: PJ.name, value: PJ.code },
                                    ]}
                                    onChange={(event) => {
                                        if(!form.isFieldsTouched()) {
                                            setLegalEntity(event.target.value);
                                            return;
                                        }
                                        setShowLegalEntityModal(true);
                                    }}
                                    value={legalEntity}
                                    optionType="button"
                                    buttonStyle="solid"
                                />
                            </Col>
                        </Row>
                    }
                    <Card>
                        <Row gutter={[24, 24]}>
                            <Col xs={24}>
                                <Form form={form} layout="vertical" name="basic" onFinish={onSave}>
                                    <Spin spinning={loading}>
                                        <Row gutter={24}>
                                            <Col xs={legalEntity === PF.code ? 8 : 24}>
                                                <Form.Item
                                                    label={legalEntity === PF.code ? "Nome" : "Razão Social"}
                                                    hasFeedback
                                                    name="name"
                                                    id="name"
                                                    rules={[{ required: true, message: "Insira " + (legalEntity === PF.code ? "um Nome" : "a Razão Social") }]}
                                                >
                                                    <Input autoComplete="off" />
                                                </Form.Item>
                                            </Col>
                                            {legalEntity === PF.code &&
                                                <React.Fragment>
                                                    <Col xs={16}>
                                                        <Form.Item
                                                            label="Sobrenome"
                                                            hasFeedback
                                                            name="surname"
                                                            rules={[{ required: true, message: "Insira o sobrenome" }]}
                                                        >
                                                            <Input autoComplete="off" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={8}>
                                                        <Form.Item
                                                            label="CPF"
                                                            hasFeedback
                                                            name="cpf"
                                                            rules={[
                                                                { required: true, message: "Insira um CPF válido" },
                                                                {
                                                                    message: "O formato do CPF está incorreto. Use o formato xxx.xxx.xxx-xx"
                                                                }
                                                            ]}
                                                        >
                                                            <InputMask mask="999.999.999-99" placeholder="000.000.000-00" autoComplete="off">
                                                                {(inputProps) => <Input {...inputProps} autoComplete="off" />}
                                                            </InputMask>
                                                        </Form.Item>
                                                    </Col>
                                                </React.Fragment>
                                            }
                                            {legalEntity === PJ.code &&
                                                <Col xs={8}>
                                                    <Form.Item
                                                        label="CNPJ"
                                                        hasFeedback
                                                        name="cnpj"
                                                        rules={[
                                                            { required: true, message: "Insira um CNPJ válido" },
                                                            {
                                                                    pattern: /^(\d{14}|\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2})$/,
                                                                    message: 'CNPJ deve estar no formato 00.000.000/0000-00'
                                                            }
                                                        ]}
                                                    >
                                                        <InputMask mask="99.999.999/9999-99" placeholder="00.000.000/0000-00">
                                                            {(inputProps) => <Input {...inputProps} autoComplete="off" />}
                                                        </InputMask>
                                                    </Form.Item>
                                                </Col>
                                            }
                                            {legalEntity === PF.code &&
                                                <React.Fragment>
                                                    <Col xs={8}>
                                                        <Form.Item
                                                            label="Data de Nascimento"
                                                            hasFeedback
                                                            name="birthday"
                                                            rules={[{ required: true, message: "Insira uma data válida" }]}
                                                        >
                                                            <DatePicker
                                                                placeholder="DD/MM/AAAA"
                                                                value={birthday ? moment(birthday, 'DD/MM/YYYY') : null}
                                                                onChange={handleDateChange}
                                                                disabledDate={(current) => {
                                                                    return current && (current > moment().endOf('day') || current >= moment().subtract(16, 'years').endOf('day'));
                                                                }}
                                                                allowClear
                                                                format="DD/MM/YYYY"
                                                                style={{ width: "100%" }}
                                                                inputReadOnly={false}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={8}>
                                                        <Form.Item
                                                            label="Renda Mensal"
                                                            hasFeedback
                                                            name="monthlyRevenueRange"
                                                            rules={[{ required: true, message: "Selecione a faixa de renda mensal." }]}
                                                        >
                                                            <Select placeholder="Selecione uma faixa">
                                                                {MONTHLY_REVENUE_RANGE_DATA.map(x => <Select.Option value={x.code}>{x.name}</Select.Option>)}
                                                            </Select>
                                                        </Form.Item>
                                                    </Col>
                                                </React.Fragment>
                                            }
                                            {legalEntity === PJ.code &&
                                                <Col xs={8}>
                                                    <Form.Item
                                                        label="Renda Anual"
                                                        hasFeedback
                                                        name="annualRevenueRange"
                                                        rules={[{ required: true, message: "Selecione a faixa de renda anual." }]}
                                                    >
                                                        <Select placeholder="Selecione uma faixa">
                                                            {ANNUAL_REVENUE_RANGE_DATA.map(x => <Select.Option value={x.code}>{x.name}</Select.Option>)}
                                                        </Select>
                                                    </Form.Item>
                                                </Col>
                                            }
                                            {legalEntity === PF.code &&
                                                <Col xs={12}>
                                                    <Form.Item
                                                        label="Nome da mãe"
                                                        hasFeedback
                                                        name="mother"
                                                        rules={[{ required: false, message: "Insira o nome da mãe" }]}
                                                    >
                                                        <Input autoComplete="off" />
                                                    </Form.Item>
                                                </Col>
                                            }
                                            <Col xs={legalEntity === PF.code ? 12 : 8}>
                                                <Form.Item
                                                    label="Tipo de Sócio"
                                                    hasFeedback
                                                    name={["shareholderType", "code"]}
                                                    rules={[{ required: true, message: "Insira o tipo de sócio" }]}
                                                >
                                                    <Select placeholder="Selecione um tipo">
                                                        {SHAREHOLDER_TYPE_DATA.map(x => <Select.Option value={x.code}>{x.name}</Select.Option>)}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col xs={16}>
                                                <Form.Item
                                                    label="E-mail"
                                                    hasFeedback
                                                    name="email"
                                                    rules={[
                                                        { required: true, message: "Insira o e-mail" },
                                                        { max: 80, message: 'Campo não deve ultrapassar 80 caracteres' }
                                                    ]}
                                                >
                                                    <Input autoComplete="off" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={8}>
                                                <Form.Item
                                                    label="Telefone"
                                                    hasFeedback
                                                    rules={[
                                                        { required: true, message: "Insira o numero de telefone" },
                                                        { pattern: /^\+55 \(\d{2}\) \d{5}-\d{4}$/, message: "Verifique o número de telefone" }
                                                    ]}
                                                    name="phone"
                                                >
                                                    <InputMask mask="+55 (99) 99999-9999" placeholder="+55 (00) 00000-0000" onChange={(event) => {
                                                            const { value } = event.target;
                                                            const cleanedValue = value.replace(/_/g, '');
                                                            const isValidFormat = /^\+55 \(\d{2}\) \d{5}-\d{4}$/.test(cleanedValue);
                                                            if (isValidFormat) {
                                                                form.setFieldsValue({ phone: cleanedValue });
                                                            } else {
                                                                form.setFieldsValue({ phone: cleanedValue.slice(0, 19) });
                                                            }
                                                        }}>
                                                        {(inputProps) => (
                                                        <Input {...inputProps} autoComplete="off" />
                                                        )}
                                                    </InputMask>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <FormAddress card={false} fullWidth={false} form={form} required={true} country={true} />
                                    </Spin>
                                </Form>
                            </Col>
                        </Row>
                    </Card>
                </Spin>
            </Modal>

            <Modal
                title="Editar Sócio"
                open={openEdit}
                onCancel={() => onCancelEdit()}
                okButtonProps={{
                    icon: <SaveOutlined />,
                    disabled: loading,
                    loading: btnLoading
                }}
                footer={[
                    <Button key="cancel" onClick={() => onCancelEdit()}>Voltar</Button>,
                    <Button key="submit" type="primary" loading={btnLoading} onClick={() => formEdit.submit()}>Salvar</Button>,
                ]}
                width={800}
            >
                <Spin spinning={loading}>
                    <Card>
                        <Row gutter={[24, 24]}>
                            <Col xs={24}>
                                <Form form={formEdit} layout="vertical" name="editShareholder" onFinish={handleEdit}>
                                    <Spin spinning={loading}>
                                        <Row gutter={24}>
                                            <Col xs={legalEntity === PF.code ? 8 : 24}>
                                                <Form.Item
                                                    label={legalEntity === PF.code ? "Nome" : "Razão Social"}
                                                    hasFeedback
                                                    name="name"
                                                    id="name"
                                                    initialValue={editingShareholder?.name}
                                                    rules={[{ required: true, message: "Insira " + (legalEntity === PF.code ? "um Nome" : "a Razão Social") }]}
                                                >
                                                    <Input autoComplete="off" />
                                                </Form.Item>
                                            </Col>
                                            {legalEntity === PF.code &&
                                                <React.Fragment>
                                                    <Col xs={16}>
                                                        <Form.Item
                                                            label="Sobrenome"
                                                            hasFeedback
                                                            name="surname"
                                                            initialValue={editingShareholder?.surname}
                                                            rules={[{ required: true, message: "Insira o sobrenome" }]}
                                                        >
                                                            <Input autoComplete="off" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={8}>
                                                        <Form.Item
                                                            label="CPF"
                                                            hasFeedback
                                                            name="cpf"
                                                            initialValue={editingShareholder?.cpf}
                                                            rules={[
                                                                { required: true, message: "Insira um CPF válido" },
                                                                {
                                                                    message: "O formato do CPF está incorreto. Use o formato xxx.xxx.xxx-xx"
                                                                }
                                                            ]}
                                                        >
                                                            <InputMask mask="999.999.999-99" placeholder="000.000.000-00" autoComplete="off">
                                                                {(inputProps) => <Input {...inputProps} autoComplete="off" />}
                                                            </InputMask>
                                                        </Form.Item>
                                                    </Col>
                                                </React.Fragment>
                                            }
                                            {legalEntity === PJ.code &&
                                                <Col xs={8}>
                                                    <Form.Item
                                                        label="CNPJ"
                                                        hasFeedback
                                                        name="cnpj"
                                                        initialValue={editingShareholder?.cnpj}
                                                        rules={[
                                                            { required: true, message: "Insira um CNPJ válido" },
                                                            {
                                                                    pattern: /^(\d{14}|\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2})$/,
                                                                    message: 'CNPJ deve estar no formato 00.000.000/0000-00'
                                                            }
                                                        ]}
                                                    >
                                                        <InputMask mask="99.999.999/9999-99" placeholder="00.000.000/0000-00">
                                                            {(inputProps) => <Input {...inputProps} autoComplete="off" />}
                                                        </InputMask>
                                                    </Form.Item>
                                                </Col>
                                            }
                                            {legalEntity === PF.code &&
                                                <React.Fragment>
                                                    <Col xs={8}>
                                                        <Form.Item
                                                            label="Data de Nascimento"
                                                            name="birthday"
                                                            rules={[{ required: true, message: "Insira uma data válida" }]}
                                                        >
                                                            <DatePicker
                                                                style={{ width: "100%" }}
                                                                format="DD/MM/YYYY"
                                                                initialValue={editingShareholder?.birthday ? moment(editingShareholder.birthday) : null}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={8}>
                                                        <Form.Item
                                                            label="Renda Mensal"
                                                            hasFeedback
                                                            name="monthlyRevenueRange"
                                                            initialValue={editingShareholder?.monthlyRevenueRange}
                                                            rules={[{ required: true, message: "Selecione a faixa de renda mensal." }]}
                                                        >
                                                            <Select placeholder="Selecione uma faixa">
                                                                {MONTHLY_REVENUE_RANGE_DATA.map(x => <Select.Option value={x.code}>{x.name}</Select.Option>)}
                                                            </Select>
                                                        </Form.Item>
                                                    </Col>
                                                </React.Fragment>
                                            }
                                            {legalEntity === PJ.code &&
                                                <Col xs={8}>
                                                    <Form.Item
                                                        label="Renda Anual"
                                                        hasFeedback
                                                        name="annualRevenueRange"
                                                        initialValue={editingShareholder?.annualRevenueRange}
                                                        rules={[{ required: true, message: "Selecione a faixa de renda anual." }]}
                                                    >
                                                        <Select placeholder="Selecione uma faixa">
                                                            {ANNUAL_REVENUE_RANGE_DATA.map(x => <Select.Option value={x.code}>{x.name}</Select.Option>)}
                                                        </Select>
                                                    </Form.Item>
                                                </Col>
                                            }
                                            {legalEntity === PF.code &&
                                                <Col xs={12}>
                                                    <Form.Item
                                                        label="Nome da mãe"
                                                        hasFeedback
                                                        name="mother"
                                                        initialValue={editingShareholder?.mother}
                                                        rules={[{ required: false, message: "Insira o nome da mãe" }]}
                                                    >
                                                        <Input autoComplete="off" />
                                                    </Form.Item>
                                                </Col>
                                            }
                                            <Col xs={legalEntity === PF.code ? 12 : 8}>
                                                <Form.Item
                                                    label="Tipo de Sócio"
                                                    hasFeedback
                                                    name={["shareholderType", "code"]}
                                                    initialValue={editingShareholder?.shareholderType}
                                                    rules={[{ required: true, message: "Insira o tipo de sócio" }]}
                                                >
                                                    <Select placeholder="Selecione um tipo">
                                                        {SHAREHOLDER_TYPE_DATA.map(x => <Select.Option value={x.code}>{x.name}</Select.Option>)}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col xs={16}>
                                                <Form.Item
                                                    label="E-mail"
                                                    hasFeedback
                                                    name="email"
                                                    initialValue={editingShareholder?.email}
                                                    rules={[
                                                        { required: true, message: "Insira o e-mail" },
                                                        { max: 80, message: 'Campo não deve ultrapassar 80 caracteres' }
                                                    ]}
                                                >
                                                    <Input autoComplete="off" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={8}>
                                                <Form.Item
                                                    label="Telefone"
                                                    hasFeedback
                                                    initialValue={editingShareholder?.phone}
                                                    rules={[
                                                        { required: true, message: "Insira o numero de telefone" },
                                                        { pattern: /^\+55 \(\d{2}\) \d{5}-\d{4}$/, message: "Verifique o número de telefone" }
                                                    ]}
                                                    name="phone"
                                                >
                                                    <InputMask mask="+55 (99) 99999-9999" placeholder="+55 (00) 00000-0000" onChange={(event) => {
                                                            const { value } = event.target;
                                                            const cleanedValue = value.replace(/_/g, '');
                                                            const isValidFormat = /^\+55 \(\d{2}\) \d{5}-\d{4}$/.test(cleanedValue);
                                                            if (isValidFormat) {
                                                                formEdit.setFieldsValue({ phone: cleanedValue });
                                                            } else {
                                                                formEdit.setFieldsValue({ phone: cleanedValue.slice(0, 19) });
                                                            }
                                                        }}>
                                                        {(inputProps) => (
                                                        <Input {...inputProps} autoComplete="off" />
                                                        )}
                                                    </InputMask>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <FormAddress card={false} fullWidth={false} form={formEdit} required={true} country={true} />
                                    </Spin>
                                </Form>
                            </Col>
                        </Row>
                    </Card>
                </Spin>
            </Modal>

            <Modal
                open={showLegalEntityModal}
                title={`Alteração de ${legalEntity != PF.code ? PF.name : PJ.name}`}
                onCancel={() =>  {
                    setShowLegalEntityModal(false)
                }}
                onOk={() => {
                    if (legalEntity === PJ.code) {
                        setLegalEntity(PF.code);
                    } else {
                        setLegalEntity(PJ.code);
                    }
                    form.resetFields();
                    setShowLegalEntityModal(false);
                }}
                width={600}
            >
                <p> Ao alterar para {legalEntity == PF.code ? PF.name : PJ.name}, os dados anteriores serão perdidos </p>
            </Modal>

            {
                id && !loading &&
                <Row gutter={[24, 24]} align={'middle'}>
                    <Col xl={12} lg={12} md={24}>
                        {!compact &&
                            <Typography.Title level={4}>Sócios</Typography.Title>
                        }
                    </Col>
                    {!loading &&
                        <Col xl={12} lg={12} md={24} style={{ textAlign: 'end' }}>
                            <Button type='primary' icon={<PlusOutlined />} onClick={() => {
                                onCancelAdd();
                                onShowAdd(true);
                            }}>Adicionar Novo Sócio</Button>
                        </Col>
                    }
                    <Col xs={24}>
                        <Table scroll={{ y: 650 }} dataSource={data.data} loading={data.loading} pagination={false} bordered>
                            <Table.Column align={"left"} title="Nome" dataIndex="name" key="name" />
                            <Table.Column align={"left"} title="CPF" dataIndex="cpf" key="cpf" />
                            <Table.Column align={"center"} title="Nascimento" dataIndex={"birthday"} key="birthday"
                                render={(value) => <FormatDate value={value} />} />
                            <Table.Column align={"center"} title="Tipo" dataIndex={["shareholderType", "name"]} key="shareholderType.name" />
                            <Table.Column align="center" title="Status" key="status" render={(value, record) => (
                                    record.biometricStatus != null && (
                                        <StatusBadge
                                            type={shareholderStatus(record).type}
                                            status={shareholderStatus(record).text}
                                            tooltipTextList={reasons(record)}
                                        />
                                    )
                                )}
                            />
                            <Table.Column fixed="right" width={200} align={"center"} title="Ações" dataIndex="id" key="id"
                                render={(value, record, index) =>
                                    <>
                                        <Tooltip title="Editar">
                                            <Button size="small" type={"link"} onClick={() => onEdit(record, value)}><EditTwoTone /></Button>
                                        </Tooltip>
                                        <Tooltip title="Remover">
                                            <Button disabled={data.data.length === 1} danger size="small" type={"link"} onClick={() => onRemove(value)}><DeleteTwoTone twoToneColor={data.data.length === 1 ? "gray" : "red"} /></Button>
                                        </Tooltip>
                                        {record.biometricStatus != null &&
                                            <React.Fragment>
                                                <BiometricStatusIcon status={record.biometricStatus} />
                                            </React.Fragment>
                                        }
                                        {record.biometricStatus != null && record.biometricStatus != "A" && record.biometricUrl != null && record.token != null &&
                                            <React.Fragment >
                                                <Tooltip title="Copiar link da biometria">
                                                    <Button
                                                        size="small"
                                                        type="link"
                                                        onClick={async () => {
                                                            try {
                                                                await navigator.clipboard.writeText(record.biometricUrl);
                                                                notification.success({ message: "Link copiado para a área de transferência!" });
                                                            } catch (err) {
                                                                notification.error({ message: "Erro ao copiar o link" });
                                                            }
                                                        }}
                                                    >
                                                        <LinkOutlined />
                                                    </Button>
                                                </Tooltip>
                                                <Tooltip title="Enviar link da biometria via e-mail">
                                                    <Button
                                                        size="small"
                                                        type="link"
                                                        onClick={async () => {
                                                            await sendEmail(record);
                                                        }}
                                                    >
                                                        <SendOutlined />
                                                    </Button>
                                                </Tooltip>
                                            </React.Fragment>
                                        }
                                    </>
                                } />
                        </Table>
                    </Col>
                </Row>
            }
        </React.Fragment >
    )
});

export default connect(mapStateToProps, {})(CompanyShareholderScreen);
